import { getSeparatedContent } from './utils/valueUtil';
import SelectTrigger from './SelectTrigger';
import Selector from './Selector';
import useSelectTriggerControl from './hooks/useSelectTriggerControl';
import useDelayReset from './hooks/useDelayReset';
import TransBtn from './TransBtn';
import Icon from '../icon';
import Tabs, { TabPane } from '../tabs';
import Checkbox from '../checkbox';
import Spin from '../spin';
import Empty from '../empty';
import { RegionTypeEnum } from '../_util/enum';
import useLock from './hooks/useLock';
import useSelectProps from './SelectContext';
import { useProvideBaseSelectProps } from './hooks/useBaseProps';
import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, onMounted, provide, ref, toRef, toRefs, watch, watchEffect, } from 'vue';
import PropTypes from '../_util/vue-types';
import { initDefaultProps, isValidElement } from '../_util/props-util';
import isMobile from '../vc-util/isMobile';
import KeyCode from '../_util/KeyCode';
import { toReactive } from '../_util/toReactive';
import classNames from '../_util/classNames';
import createRef from '../_util/createRef';
import useInjectLegacySelectContext from '../vc-tree-select/LegacyContext';
import { cloneElement } from '../_util/vnode';
import { useLocaleReceiver } from '../locale-provider/LocaleReceiver';
import defaultLocale from '../locale/zh_CN'; // 默认使用中文
import _ from 'lodash';
const DEFAULT_OMIT_PROPS = [
    'value',
    'onChange',
    'removeIcon',
    'placeholder',
    'autofocus',
    'maxTagCount',
    'flexNoWrap',
    'maxTagTextLength',
    'maxTagPlaceholder',
    'choiceTransitionName',
    'onInputKeyDown',
    'onPopupScroll',
    'onSetOptions',
    'onSetMergedOpen',
    'tabindex',
    'OptionList',
    'notFoundContent',
];
const baseSelectPrivateProps = () => {
    return {
        prefixCls: String,
        id: String,
        omitDomProps: Array,
        // >>> Value
        displayValues: Array,
        stockSearch: Boolean,
        searchIconPlacement: { type: String, default: 'right' },
        regionType: {
            type: Number,
        },
        stockField: {
            type: Object,
            default: { stockCode: 'stockCode', stockName: 'stockName', regionType: 'regionType' },
        },
        stockSearchHeader: Boolean,
        history: Boolean,
        locale: { type: Object, default: undefined },
        spin: { type: [Boolean, Object], default: undefined },
        onDisplayValuesChange: Function,
        // >>> Active
        /** Current dropdown list active item string value */
        activeValue: String,
        /** Link search input with target element */
        activeDescendantId: String,
        onActiveValueChange: Function,
        // >>> Search
        searchValue: String,
        /** Trigger onSearch, return false to prevent trigger open event */
        onSearch: Function,
        /** Trigger when search text match the `tokenSeparators`. Will provide split content */
        onSearchSplit: Function,
        maxLength: Number,
        OptionList: PropTypes.any,
        /** Tell if provided `options` is empty */
        emptyOptions: Boolean,
    };
};
export const baseSelectPropsWithoutPrivate = () => {
    return {
        showSearch: { type: Boolean, default: undefined },
        tagRender: { type: Function },
        optionLabelRender: { type: Function },
        direction: { type: String },
        // MISC
        tabindex: Number,
        autofocus: Boolean,
        notFoundContent: PropTypes.any,
        placeholder: PropTypes.any,
        onClear: Function,
        choiceTransitionName: String,
        // >>> Mode
        mode: String,
        // >>> Status
        disabled: { type: Boolean, default: undefined },
        loading: { type: Boolean, default: undefined },
        // >>> Open
        open: { type: Boolean, default: undefined },
        defaultOpen: { type: Boolean, default: undefined },
        onDropdownVisibleChange: { type: Function },
        onCheckAllChange: { type: Function },
        onHeaderTabChange: { type: Function },
        onSetOptions: { type: Function },
        onSetMergedOpen: { type: Function },
        // >>> Customize Input
        /** @private Internal usage. Do not use in your production. */
        getInputElement: { type: Function },
        /** @private Internal usage. Do not use in your production. */
        getRawInputElement: { type: Function },
        // >>> Selector
        maxTagTextLength: Number,
        maxTagCount: { type: [String, Number] },
        maxTagPlaceholder: PropTypes.any,
        flexNoWrap: Boolean,
        // >>> Search
        tokenSeparators: { type: Array },
        // >>> Icons
        allowClear: { type: Boolean, default: undefined },
        showArrow: { type: Boolean, default: undefined },
        inputIcon: PropTypes.any,
        /** Clear all icon */
        clearIcon: PropTypes.any,
        /** Selector remove icon */
        removeIcon: PropTypes.any,
        // >>> Dropdown
        animation: String,
        transitionName: String,
        dropdownStyle: { type: Object },
        dropdownClassName: String,
        dropdownMatchSelectWidth: {
            type: [Boolean, Number],
            default: undefined,
        },
        dropdownRender: { type: Function },
        dropdownAlign: Object,
        placement: {
            type: String,
        },
        getPopupContainer: { type: Function },
        // >>> Focus
        showAction: { type: Array },
        onBlur: { type: Function },
        onFocus: { type: Function },
        // >>> Rest Events
        onKeyup: Function,
        onKeydown: Function,
        onMousedown: Function,
        onPopupScroll: Function,
        onInputKeyDown: Function,
        onMouseenter: Function,
        onMouseleave: Function,
        onClick: Function,
        label: String,
        condition: { type: Boolean, default: undefined },
    };
};
const baseSelectProps = () => {
    return {
        ...baseSelectPrivateProps(),
        ...baseSelectPropsWithoutPrivate(),
    };
};
export function isMultiple(mode) {
    return mode === 'tags' || mode === 'multiple';
}
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'BaseSelect',
    inheritAttrs: false,
    props: initDefaultProps(baseSelectProps(), { showAction: [], notFoundContent: 'Not Found' }),
    setup(props, { attrs, expose, slots }) {
        const multiple = computed(() => isMultiple(props.mode));
        const mergedShowSearch = computed(() => props.showSearch !== undefined
            ? props.showSearch
            : multiple.value || props.mode === 'combobox');
        const mobile = ref(false);
        onMounted(() => {
            mobile.value = isMobile();
        });
        const legacyTreeSelectContext = useInjectLegacySelectContext();
        const selectProps = useSelectProps();
        // ============================== Refs ==============================
        const containerRef = ref(null);
        const selectorDomRef = createRef();
        const triggerRef = ref(null);
        const selectorRef = ref(null);
        const listRef = ref(null);
        const [selectLocale] = useLocaleReceiver('Select', defaultLocale.Select, toRef(props, 'locale'));
        /** Used for component focused management */
        const [mockFocused, setMockFocused, cancelSetMockFocused] = useDelayReset();
        const focus = () => {
            selectorRef.value?.focus();
        };
        const blur = () => {
            selectorRef.value?.blur();
        };
        const preDisplayValues = ref(null);
        expose({
            focus,
            blur,
            scrollTo: arg => listRef.value?.scrollTo(arg),
        });
        // 股票搜索组件选中的id
        // const stockSearchMergedSearchValue = computed(() => {
        //   return props.displayValues[0]?.value;
        // });
        // ============================== Open ==============================
        const initOpen = props.open !== undefined ? props.open : props.defaultOpen;
        const innerOpen = ref(initOpen);
        const mergedOpen = ref(initOpen);
        const setInnerOpen = (val) => {
            innerOpen.value = props.open !== undefined ? props.open : val;
            mergedOpen.value = innerOpen.value;
        };
        watch(() => props.open, () => {
            setInnerOpen(props.open);
        });
        // 股票搜索组件回显label，其余select回显value(此value非业务传进来的value)
        const mergedSearchValue = computed(() => {
            if (props.mode !== 'combobox') {
                return props.searchValue;
            }
            const val = props.stockSearch && (!mergedOpen.value || (mergedOpen.value && flag.value))
                ? props.displayValues[0]?.label
                : props.displayValues[0]?.value;
            return typeof val === 'string' || typeof val === 'number' ? String(val) : '';
        });
        // Not trigger `open` in `combobox` when `notFoundContent` is empty
        const emptyListContent = computed(() => {
            return props.stockSearch ? !mergedOpen.value : !props.notFoundContent && props.emptyOptions;
        });
        watchEffect(() => {
            mergedOpen.value = innerOpen.value;
            if (props.disabled ||
                (emptyListContent.value && mergedOpen.value && props.mode === 'combobox')) {
                mergedOpen.value = false;
            }
        });
        const triggerOpen = computed(() => (emptyListContent.value ? false : mergedOpen.value));
        const onToggleOpen = (newOpen) => {
            props.onSetMergedOpen && props.onSetMergedOpen(mergedOpen.value);
            const nextOpen = newOpen !== undefined ? newOpen : !mergedOpen.value;
            if (innerOpen.value !== nextOpen && !props.disabled) {
                setInnerOpen(nextOpen);
                if (props.onDropdownVisibleChange) {
                    props.onDropdownVisibleChange(nextOpen);
                }
            }
        };
        const tokenWithEnter = computed(() => (props.tokenSeparators || []).some(tokenSeparator => ['\n', '\r\n'].includes(tokenSeparator)));
        const flag = ref(false);
        const onInternalSearch = (searchText, fromTyping, isCompositing) => {
            flag.value = false;
            let ret = true;
            let newSearchText = searchText;
            props.onActiveValueChange?.(null);
            // Check if match the `tokenSeparators`
            const patchLabels = isCompositing
                ? null
                : getSeparatedContent(searchText, props.tokenSeparators);
            // Ignore combobox since it's not split-able
            if (props.mode !== 'combobox' && patchLabels) {
                newSearchText = '';
                props.onSearchSplit?.(patchLabels);
                // Should close when paste finish
                onToggleOpen(false);
                // Tell Selector that break next actions
                ret = false;
            }
            if (props.onSearch && mergedSearchValue.value !== newSearchText) {
                props.onSearch(newSearchText, {
                    source: fromTyping ? 'typing' : 'effect',
                });
            }
            return ret;
        };
        // Only triggered when menu is closed & mode is tags
        // If menu is open, OptionList will take charge
        // If mode isn't tags, press enter is not meaningful when you can't see any option
        const onInternalSearchSubmit = (searchText) => {
            // prevent empty tags from appearing when you click the Enter button
            if (!searchText || !searchText.trim()) {
                return;
            }
            props.onSearch?.(searchText, { source: 'submit' });
        };
        // Close will clean up single mode search text
        watch(mergedOpen, () => {
            if (!mergedOpen.value && !multiple.value && props.mode !== 'combobox') {
                onInternalSearch('', false, false);
            }
        }, { immediate: true, flush: 'post' });
        // ============================ Disabled ============================
        // Close dropdown & remove focus state when disabled change
        watch(() => props.disabled, () => {
            if (innerOpen.value && !!props.disabled) {
                setInnerOpen(false);
            }
        }, { immediate: true });
        // ============================ Keyboard ============================
        /**
         * We record input value here to check if can press to clean up by backspace
         * - null: Key is not down, this is reset by key up
         * - true: Search text is empty when first time backspace down
         * - false: Search text is not empty when first time backspace down
         */
        const [getClearLock, setClearLock] = useLock();
        // KeyDown
        const onInternalKeyDown = (event, ...rest) => {
            const clearLock = getClearLock();
            const { which } = event;
            if (which === KeyCode.ENTER) {
                if (props.stockSearch) {
                    return;
                }
                // Do not submit form when type in the input
                if (props.mode !== 'combobox') {
                    event.preventDefault();
                }
                // We only manage open state here, close logic should handle by list component
                if (!mergedOpen.value) {
                    onToggleOpen(true);
                }
            }
            setClearLock(!!mergedSearchValue.value);
            // Remove value by `backspace`
            if (which === KeyCode.BACKSPACE &&
                !clearLock &&
                multiple.value &&
                !mergedSearchValue.value &&
                props.displayValues.length) {
                const cloneDisplayValues = [...props.displayValues];
                let removedDisplayValue = null;
                for (let i = cloneDisplayValues.length - 1; i >= 0; i -= 1) {
                    const current = cloneDisplayValues[i];
                    if (!current.disabled) {
                        cloneDisplayValues.splice(i, 1);
                        removedDisplayValue = current;
                        break;
                    }
                }
                if (removedDisplayValue) {
                    props.onDisplayValuesChange(cloneDisplayValues, {
                        type: 'remove',
                        values: [removedDisplayValue],
                    });
                }
            }
            if (mergedOpen.value && listRef.value) {
                listRef.value.onKeydown(event, ...rest);
            }
            props.onKeydown?.(event, ...rest);
        };
        // KeyUp
        const onInternalKeyUp = (event, ...rest) => {
            if (mergedOpen.value && listRef.value) {
                listRef.value.onKeyup(event, ...rest);
            }
            if (props.onKeyup) {
                props.onKeyup(event, ...rest);
            }
        };
        // ============================ Selector ============================
        const onSelectorRemove = (val) => {
            const newValues = props.displayValues.filter(i => i !== val);
            props.onDisplayValuesChange(newValues, {
                type: 'remove',
                values: [val],
            });
        };
        // ========================== Focus / Blur ==========================
        /** Record real focus status */
        const focusRef = ref(false);
        const isSelected = ref(false);
        const onToggleSelected = (val) => {
            isSelected.value = val;
        };
        const onContainerFocus = (...args) => {
            flag.value = true;
            preDisplayValues.value = props.displayValues[0];
            onToggleSelected(false);
            setMockFocused(true);
            if (!props.disabled) {
                if (props.onFocus && !focusRef.value) {
                    props.onFocus(...args);
                }
                // `showAction` should handle `focus` if set
                if (props.showAction && props.showAction.includes('focus')) {
                    onToggleOpen(true);
                }
            }
            focusRef.value = true;
        };
        const onContainerBlur = (...args) => {
            flag.value = false;
            setMockFocused(false, () => {
                focusRef.value = false;
                onToggleOpen(false);
            });
            if (props.disabled) {
                return;
            }
            const searchVal = mergedSearchValue.value;
            if (searchVal) {
                // `tags` mode should move `searchValue` into values
                if (props.mode === 'tags') {
                    props.onSearch(searchVal, { source: 'submit' });
                }
                else if (props.mode === 'multiple') {
                    // `multiple` mode only clean the search value but not trigger event
                    props.onSearch('', {
                        source: 'blur',
                    });
                }
            }
            // 股票搜索组件只有选中了id,才回显
            // if (props.stockSearch && !props.displayValues[0]?.option) {
            if (props.stockSearch && !isSelected.value) {
                if (preDisplayValues.value) {
                    // 还需要回填valueOptions  （optionList）
                    const valueCopy = _.cloneDeep(preDisplayValues.value);
                    if (!valueCopy.option) {
                        valueCopy.option = {
                            stockId: valueCopy.value,
                            [props.stockField.stockCode]: valueCopy.label,
                            value: valueCopy.label,
                            label: valueCopy.value,
                        };
                    }
                    props.onSetOptions(valueCopy);
                    selectProps.onSelect(valueCopy, { selected: false });
                }
                else {
                    props.onDisplayValuesChange([], {
                        type: 'clear',
                        values: props.displayValues,
                    });
                    onInternalSearch('', false, false);
                }
            }
            if (props.onBlur) {
                props.onBlur(...args);
            }
        };
        provide('VCSelectContainerEvent', {
            focus: onContainerFocus,
            blur: onContainerBlur,
        });
        // Give focus back of Select
        const activeTimeoutIds = [];
        onMounted(() => {
            activeTimeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
            activeTimeoutIds.splice(0, activeTimeoutIds.length);
        });
        onBeforeUnmount(() => {
            activeTimeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
            activeTimeoutIds.splice(0, activeTimeoutIds.length);
        });
        const onInternalMouseDown = (event, ...restArgs) => {
            const { target } = event;
            const popupElement = triggerRef.value?.getPopupElement();
            // We should give focus back to selector if clicked item is not focusable
            if (popupElement && popupElement.contains(target)) {
                const timeoutId = setTimeout(() => {
                    const index = activeTimeoutIds.indexOf(timeoutId);
                    if (index !== -1) {
                        activeTimeoutIds.splice(index, 1);
                    }
                    cancelSetMockFocused();
                    if (!mobile.value && !popupElement.contains(document.activeElement)) {
                        selectorRef.value?.focus();
                    }
                });
                activeTimeoutIds.push(timeoutId);
            }
            props.onMousedown?.(event, ...restArgs);
        };
        // ============================= Dropdown ==============================
        const containerWidth = ref(null);
        const instance = getCurrentInstance();
        const onPopupMouseEnter = () => {
            // We need force update here since popup dom is render async
            instance.update();
        };
        onMounted(() => {
            watch(triggerOpen, () => {
                if (triggerOpen.value) {
                    const newWidth = Math.ceil(containerRef.value?.offsetWidth);
                    if (containerWidth.value !== newWidth && !Number.isNaN(newWidth)) {
                        containerWidth.value = newWidth;
                    }
                }
            }, { immediate: true, flush: 'post' });
        });
        // Close when click on non-select element
        useSelectTriggerControl([containerRef, triggerRef], triggerOpen, onToggleOpen);
        useProvideBaseSelectProps(toReactive({
            ...toRefs(props),
            open: mergedOpen,
            triggerOpen,
            showSearch: mergedShowSearch,
            multiple,
            toggleOpen: onToggleOpen,
            toggleSelected: onToggleSelected,
            blur,
        }));
        return () => {
            const { prefixCls, id, open, defaultOpen, mode, 
            // Search related
            showSearch, searchValue, onSearch, 
            // Icons
            allowClear, clearIcon, showArrow, inputIcon, 
            // Others
            disabled, loading, getInputElement, getPopupContainer, placement, 
            // Dropdown
            animation, transitionName, dropdownStyle, dropdownClassName, dropdownMatchSelectWidth, dropdownRender, dropdownAlign, showAction, direction, 
            // Tags
            tokenSeparators, tagRender, optionLabelRender, 
            // Events
            onPopupScroll, onDropdownVisibleChange, onCheckAllChange, onHeaderTabChange, onFocus, onBlur, onKeyup, onKeydown, onMousedown, onClear, omitDomProps, getRawInputElement, displayValues, onDisplayValuesChange, emptyOptions, activeDescendantId, activeValue, OptionList, label, condition, maxTagCount, flexNoWrap, ...restProps } = { ...props, ...attrs };
            // ============================= Input ==============================
            // Only works in `combobox`
            const customizeInputElement = (mode === 'combobox' && getInputElement && getInputElement()) || null;
            // Used for customize replacement for `vc-cascader`
            const customizeRawInputElement = typeof getRawInputElement === 'function' && getRawInputElement();
            const domProps = {
                ...restProps,
            };
            // Used for raw custom input trigger
            let onTriggerVisibleChange;
            if (customizeRawInputElement) {
                onTriggerVisibleChange = (newOpen) => {
                    onToggleOpen(newOpen);
                };
            }
            DEFAULT_OMIT_PROPS.forEach(propName => {
                delete domProps[propName];
            });
            omitDomProps?.forEach(propName => {
                delete domProps[propName];
            });
            // ============================= Arrow ==============================
            const mergedShowArrow = true;
            // 暂时注释 面性默认没有箭头
            // showArrow !== undefined ? showArrow : loading || (!multiple.value && mode !== 'combobox');
            let arrowNode;
            if (mergedShowArrow) {
                arrowNode = (<TransBtn class={classNames(`${prefixCls}-arrow`, {
                        [`${prefixCls}-arrow-loading`]: loading,
                    })} customizeIcon={inputIcon} customizeIconProps={{
                        loading,
                        searchValue: mergedSearchValue.value,
                        open: mergedOpen.value,
                        focused: mockFocused.value,
                        showSearch: mergedShowSearch.value,
                    }}/>);
            }
            let prefixIcon;
            if (props.stockSearch && props.searchIconPlacement === 'left') {
                prefixIcon = (<span class={`${prefixCls}-prefix`}>
            <Icon class={`${prefixCls}-suffix-search`} iconClass="wd-icon-interactive_search"/>
          </span>);
            }
            // ============================= Clear ==============================
            let clearNode;
            const onClearMouseDown = () => {
                onClear?.();
                onDisplayValuesChange([], {
                    type: 'clear',
                    values: displayValues,
                });
                onInternalSearch('', false, false);
                preDisplayValues.value = null;
            };
            if (!disabled && allowClear && (displayValues.length || mergedSearchValue.value)) {
                clearNode = (<TransBtn class={`${prefixCls}-clear`} onMousedown={onClearMouseDown} customizeIcon={clearIcon}>
            ×
          </TransBtn>);
            }
            // >>>>>>>>> Spinning
            let spinProps;
            if (typeof props.spin === 'boolean') {
                spinProps = {
                    spinning: props.spin,
                    placement: 'top',
                };
            }
            else if (typeof props.spin === 'object') {
                spinProps = {
                    spinning: true,
                    ...props.spin,
                };
            }
            const onListMouseDown = event => {
                event.preventDefault();
            };
            // =========================== OptionList ===========================
            const optionListSpin = (<Spin spinning={false} {...spinProps} placement="">
          {props.emptyOptions && props.stockSearch ? (<div onMousedown={onListMouseDown}>
              <Empty type="module" style={{
                        height: '320px',
                        margin: 0,
                        padding: '16px 0',
                    }}/>
            </div>) : multiple.value && props?.onCheckAllChange ? (<div>
              <OptionList ref={listRef} v-slots={{ ...legacyTreeSelectContext.customSlots, option: slots.option }}/>
              <Checkbox class={`${prefixCls}-dropdown-checkall`} onChange={() => props.onCheckAllChange?.(props.displayValues.length !== selectProps.options.length)} checked={props.displayValues.length === selectProps.options.length} indeterminate={props.displayValues.length > 0 &&
                        props.displayValues.length < selectProps.options.length}>
                {selectLocale.value.checkAll}
              </Checkbox>
            </div>) : (<OptionList ref={listRef} v-slots={{ ...legacyTreeSelectContext.customSlots, option: slots.option }}/>)}
        </Spin>);
            const optionList = props.stockSearchHeader || props.history ? (<div>
            {selectorRef.value?.historyShow ? (<p onMousedown={onListMouseDown} class={`${prefixCls}-dropdown-stock-search-history`}>
                {selectLocale.value.history}
              </p>) : (<>
                {props.stockSearchHeader ? (<div>
                    <Tabs class={`${prefixCls}-dropdown-stock-search-header`} centered onMousedown={onListMouseDown} onChange={props.onHeaderTabChange}>
                      <TabPane key={''} tab={selectLocale.value.regionTypeAll}/>
                      <TabPane key={RegionTypeEnum.HK} tab={selectLocale.value.regionTypeHK}/>
                      <TabPane key={RegionTypeEnum.US} tab={selectLocale.value.regionTypeUS}/>
                    </Tabs>

                    <p class={`${prefixCls}-dropdown-stock-search-place`} onMousedown={onListMouseDown}></p>
                  </div>) : ('')}
              </>)}
            {optionListSpin}
          </div>) : (optionListSpin);
            // ============================= Select =============================
            const mergedClassName = classNames(prefixCls, attrs.class, {
                [`${prefixCls}-focused`]: mockFocused.value,
                [`${prefixCls}-multiple`]: multiple.value,
                [`${prefixCls}-single`]: !multiple.value,
                [`${prefixCls}-allow-clear`]: allowClear,
                [`${prefixCls}-show-arrow`]: mergedShowArrow,
                [`${prefixCls}-show-prefix`]: props.stockSearch && props.searchIconPlacement === 'left',
                [`${prefixCls}-disabled`]: disabled,
                [`${prefixCls}-loading`]: loading,
                [`${prefixCls}-open`]: mergedOpen.value,
                [`${prefixCls}-customize-input`]: customizeInputElement,
                [`${prefixCls}-show-search`]: mergedShowSearch.value,
                [`${prefixCls}-condition`]: condition,
                [`${prefixCls}-max-tag-count`]: maxTagCount,
                [`${prefixCls}-flex-no-wrap`]: flexNoWrap,
                [`${prefixCls}-fill`]: displayValues.length || mergedSearchValue.value, // 被填充 存在值时
            });
            // >>> Selector
            const selectorNode = (<SelectTrigger ref={triggerRef} disabled={disabled} prefixCls={prefixCls} visible={triggerOpen.value} popupElement={optionList} containerWidth={containerWidth.value} animation={animation} transitionName={transitionName} dropdownStyle={dropdownStyle} dropdownClassName={dropdownClassName} direction={direction} dropdownMatchSelectWidth={dropdownMatchSelectWidth} dropdownRender={dropdownRender} dropdownAlign={dropdownAlign} placement={placement} getPopupContainer={getPopupContainer} empty={emptyOptions} getTriggerDOMNode={() => selectorDomRef.current} onPopupVisibleChange={onTriggerVisibleChange} onPopupMouseEnter={onPopupMouseEnter} v-slots={{
                    default: () => {
                        return customizeRawInputElement ? (isValidElement(customizeRawInputElement) &&
                            cloneElement(customizeRawInputElement, {
                                ref: selectorDomRef,
                            }, false, true)) : (<Selector {...props} domRef={selectorDomRef} prefixCls={prefixCls} inputElement={customizeInputElement} ref={selectorRef} id={id} showSearch={mergedShowSearch.value} mode={mode} activeDescendantId={activeDescendantId} tagRender={tagRender} optionLabelRender={optionLabelRender} values={displayValues} open={mergedOpen.value} originalOpen={props.open} onToggleOpen={onToggleOpen} onToggleSelected={onToggleSelected} activeValue={activeValue} searchValue={mergedSearchValue.value} onSearch={onInternalSearch} onSearchSubmit={onInternalSearchSubmit} onRemove={onSelectorRemove} tokenWithEnter={tokenWithEnter.value}/>);
                    },
                }}></SelectTrigger>);
            // >>> Render
            let renderNode;
            // Render raw
            if (customizeRawInputElement) {
                renderNode = selectorNode;
            }
            else {
                renderNode = (<div {...domProps} class={mergedClassName} ref={containerRef} onMousedown={onInternalMouseDown} onKeydown={onInternalKeyDown} onKeyup={onInternalKeyUp}>
            {mockFocused.value && !mergedOpen.value && (<span style={{
                            width: 0,
                            height: 0,
                            display: 'flex',
                            overflow: 'hidden',
                            opacity: 0,
                        }} aria-live="polite">
                {/* Merge into one string to make screen reader work as expect */}
                {`${displayValues
                            .map(({ label, value }) => ['number', 'string'].includes(typeof label) ? label : value)
                            .join(', ')}`}
              </span>)}
            {prefixIcon}
            {selectorNode}
            {arrowNode}
            {clearNode}
          </div>);
            }
            return renderNode;
        };
    },
});
